import posthog from 'posthog-js';
import { useState, useEffect } from "react";
import { useLocation, useHistory, Switch, Route, Redirect } from "react-router-dom";
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";
import Cookies from "universal-cookie";
import { Auth } from "./Component/Api/Auth/AuthService";
import { Interceptors } from "./Component/Api/AxiosInterceptor";
import { IntermediateGlobalLoading } from "./Component/Common/Components/Loading/IntermediateGlobalLoading";
import { RouterBase } from "./Component/Common/routerBaseComponent";
import { ForgotPasswordPage } from "./Modules/Base/forgotPasswordPage";
import { LoginPage } from "./Modules/Base/loginPage";
import { UserAtom } from "./State/user.Atom";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./assets/theme";
import { CssBaseline } from "@mui/material";
import { CoreInterceptors } from "./Component/Api/AxiosCoreInterceptor";
import { ScorecardAtom } from "./State/Scorecard.Atom";
import { EEScorecardAtom } from "./State/EEScorecard.Atom";
import { ConsolidationAtom } from "./State/Consolidation.Atom";
import { RolesAtoms } from "./State/Roles.Atom";
import { TwoFactorPage } from "./Modules/Base/TwoFactor/TwoFactorPage";
import { VerificationAgencyApplicationPageForExternalUse } from "./Modules/VerificationAgency/Applications/VerificationAgencyApplicationPageForExternalUse";
import { FeedbackPageForExternalUse } from "./Modules/VerificationAgency/Feedback/External/FeedbackPageForExternalUse";
import { VerificationCompanyAtom } from "./State/VerificationCompany.Atom";
import { coreTokenAtom, tokenAtom } from "./State/Token.Atom";
import { resetActivityTimer } from "./Services/SessionActivityTracker";
import { showInfoDialog } from "./Component/Common/Components/SweetAlert/SweetAlert";
import config from "./config";
import { isNullOrEmpty } from "./Component/Model/helpers";
import { WSPInterceptors } from "./Component/Api/AxiosWSPInterceptor";
import { WSPScorecardAtom } from "./State/WSPScorecard.Atom";
import { OrganisationAtom } from "./State/Organisation.Atom";
import { BBBEEScorecardAtom } from "./State/BBBEEScorecard.Atom";
import { CacheBuster } from '../src/Cache/CacheBuster';
import { environment } from "./environment.version";
import { WSPScorecardService } from "./Component/Api/WSPScorecard/WSPScorecardService";
import { ClientAdvisorAtom } from "./State/Advisory/ClientAdvisors.Atom";
import { AdvisoryService } from "./Component/Api/Advisory/AdvisoryService";
import { ClientRegistrationAtom } from "./State/Advisory/ClientRegistration.Atom";

export const App: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const setUser = useSetRecoilState(UserAtom);
  const location = useLocation();
  const history = useHistory();
  const resetScorecard = useResetRecoilState(ScorecardAtom);
  const resetScorecardType = useResetRecoilState(ConsolidationAtom);
  const resetScorecardData = useResetRecoilState(EEScorecardAtom);
  const setRoles = useSetRecoilState(RolesAtoms);
  const resetVerificationCompany = useResetRecoilState(VerificationCompanyAtom);
  const resetWSPScorecard = useResetRecoilState(WSPScorecardAtom);
  const resetClientRegistration = useResetRecoilState(ClientRegistrationAtom);
  const resetOrganisationAtom = useResetRecoilState(OrganisationAtom);
  const resetBBBEEScorecard = useResetRecoilState(BBBEEScorecardAtom);
  const [token, setToken] = useRecoilState<string>(tokenAtom);
  const [coreToken, setCoreToken] = useRecoilState<string>(coreTokenAtom);
  const setClientAdvisors = useSetRecoilState(ClientAdvisorAtom);

  const IDLE_TIMEOUT = 60 * parseInt(config.cookieTimeoutInMinutes) * 1000;

  useEffect(() => {
    if (posthog.__loaded) posthog.capture('$pageview');
  }, [location]);

  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      if (!posthog.__loaded) return;
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          if (!(node instanceof HTMLElement)) continue;
          const dialog = node.getAttribute('role') === 'dialog'
            ? node
            : node.querySelector('[role="dialog"]');
          if (dialog) {
            const title = dialog.querySelector('.MuiDialogTitle-root')?.textContent?.trim() ?? 'unknown';
            posthog.capture('modal_opened', { modal: title });
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // Set token upon application open
  useEffect(() => {
    Interceptors.setupUnauthorizedInterceptor(history as any);
    CoreInterceptors.setupUnauthorizedCoreInterceptor(history as any);
    WSPInterceptors.setupUnauthorizedCoreInterceptor(history as any);
  }, [history]);

  useEffect(() => {
    if (!isNullOrEmpty(token) && !isNullOrEmpty(coreToken)) {
      localStorage.setItem('__website', '9b3beced97c186052bc6375408c5c2ed')
      if (!location.pathname.includes('/ForgotPassword') && !location.pathname.includes('/VerificationAgencyApplication') && !location.pathname.includes('/VerificationAgencyFeedback')) {
        setLoading(true);
        GetCsfr();
        GetUserInfo();
      }
    }
  }, [token, coreToken]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleMouseMovement = () => {
      resetActivityTimer();
    };

    const handleKeyPress = () => {
      resetActivityTimer();
    };

    document.addEventListener('mousemove', handleMouseMovement);
    document.addEventListener('keypress', handleKeyPress);

    return () => {
      document.removeEventListener('mousemove', handleMouseMovement);
      document.removeEventListener('keypress', handleKeyPress);
    };
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    startIdleTimeoutCheck();

    return () => {
      localStorage.setItem('sessionTimeoutChecked', 'false');
    };
  }, [token, coreToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const getLastActivityTime = () => {
    const lastActivityTime = localStorage.getItem("sessionExpirationTime");
    return lastActivityTime ? parseInt(lastActivityTime) : null;
  };

  const checkInactivity = () => {
    const lastActivityTime = getLastActivityTime();
    if (!lastActivityTime || isNullOrEmpty(token) || isNullOrEmpty(coreToken)) {
      return;
    }

    const currentTime = Date.now();
    const elapsedTime = currentTime - lastActivityTime;

    const checkedForTimeoutKey = localStorage.getItem('sessionTimeoutChecked');
    const checkedForTimeout = checkedForTimeoutKey === 'true' ? true : false;

    if (elapsedTime >= IDLE_TIMEOUT && !checkedForTimeout) {
      localStorage.setItem('sessionTimeoutChecked', 'true');
      resetAllData();
      showInfoDialog(
        'Session Timeout',
        'Your online session has expired. Please click "Continue" to be taken to the login page.',
        'Continue',
        () => {
          try {
            localStorage.setItem('sessionTimeoutChecked', 'false');
            history.push('/login');
          } catch (error) {
            console.log(error);
          }
        }
      );
    }
  };

  const startIdleTimeoutCheck = () => {
    setInterval(checkInactivity, 100);
  };

  const resetAllData = () => {
    resetScorecard();
    resetScorecardData();
    resetScorecardType();
    resetVerificationCompany();
    resetWSPScorecard();
    resetClientRegistration();
    resetOrganisationAtom();
    resetBBBEEScorecard();
    localStorage.removeItem('coreToken');
    localStorage.removeItem('accessToken');
    setToken('');
    setCoreToken('');
  }

  function GetCsfr() {
    try {
      Auth.Csfr().then(result => {
        const cookies = new Cookies();
        var ExistingToken = localStorage.getItem('__RequestVerificationToken');
        if (ExistingToken) {
          localStorage.removeItem('__RequestVerificationToken');
          localStorage.removeItem('__formtoken');
          localStorage.setItem('__RequestVerificationToken', result.data.CookieToken);
          localStorage.setItem('__formtoken', result.data.FormToken);
        } else {
          localStorage.setItem('__RequestVerificationToken', result.data.CookieToken);
          localStorage.setItem('__formtoken', result.data.FormToken);
        }
        cookies.remove('__RequestVerificationToken');
        cookies.remove('__formtoken');
        setLoading(false);
      });
    } catch (error) {
      setLoading(false);
      resetScorecard();
      resetScorecardData();
      resetScorecardType();
      resetWSPScorecard();
      resetClientRegistration();
      resetBBBEEScorecard();
    }
  }

  const GetUserInfo = async () => {
    try {
      let accountDetailsPromise = Auth.getAccountDetails();
      let hasWSPScorecardsPromise = WSPScorecardService.checkRegistrationHasScorecards();
      let [accountDetailsResponse, hasWSPScorecardsResponse] = await Promise.all([
        accountDetailsPromise,
        hasWSPScorecardsPromise
      ])
      setRoles(accountDetailsResponse.data.roles);
      const newUserState = { ...accountDetailsResponse.data, hasWSPScorecards: hasWSPScorecardsResponse.data };
      setUser(newUserState);
      if (posthog.__loaded) posthog.identify(posthog.get_distinct_id(), {
        team: newUserState.email?.toLowerCase().endsWith('@bee123.co.za') ? 'bee123' : undefined
      });
      getClientAdvisors(newUserState.isAdmin);
    } catch (e) {
    }
    finally {
      setLoading(false);
    }
  }

  const getClientAdvisors = async (isAdmin: boolean) => {
    try{
      if (isAdmin){
        const response = await AdvisoryService.getClientAdvisorLinks();
        setClientAdvisors(response.data)
      }

    }catch (error){
      console.error(error)
    }
  }

  const isDevelopment = config.environment === 'local';

  return (
    <CacheBuster
      currentVersion={environment.VERSION}
      isEnabled={!isDevelopment}
      isVerboseMode={config.environment !== 'live'}
      loadingComponent={<IntermediateGlobalLoading />}
      metaFileDirectory={config.publicUrl}
    >
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {loading ? <IntermediateGlobalLoading /> :
          <div>
            <Switch>
              <Route path={'/login/:state?/:code?'} component={LoginPage} />
              <Route path={'/twoFactorAuthentication'} component={TwoFactorPage} />
              <Route path='/ForgotPassword/:id' component={ForgotPasswordPage} />
              <Route path={'/VerificationAgencyApplication/:token'} component={VerificationAgencyApplicationPageForExternalUse} />
              <Route path={'/VerificationAgencyFeedback/:token'} component={FeedbackPageForExternalUse} />
              <Route path='/' component={RouterBase} />
              <Redirect from="/" to="/home" />
            </Switch>
          </div>
        }
      </ThemeProvider>
    </CacheBuster>
  );
}